import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import {
  PositionColumn,
  Column,
  UserNameHeader,
  Row,
  Table,
  Th,
  Status
} from './StyledGroupTable';

import SocialMediaIcon from 'components/icons/SocialMediaIcon';

import {
  StyledTooltipWrapper,
  TooltipMessage
} from 'components/TooltipWrapper/StyledTooltipGroup';

import EmployeeAvatar from 'images/avatars/employee.png';
import iconTrash from 'images/icons/trash.png';
import Icon from 'components/icons/Icon';

import { Colors } from 'styles/Constants';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { deleteAmbassador } from 'services/rest/Ambassador/deleteAmbassador';
import { getGroupById } from 'services/rest/Group/getGroupById';
import { GroupCard } from 'pages/GroupAdminPage/GroupAdminCard/GroupAdminCard';
import StatusIcon from 'components/icons/StatusIcon';

interface GroupParams {
  groupId: string;
  origin_group: string;
}
interface Group {
  label: string;
  checked: boolean;
}

export interface Employee {
  social_network: any;
  id: number;
  name: string;
  lastName: string;
  email?: string;
  phone?: string;
  photo: string;
  language?: string;
  password?: string;
  role?: string;
  linkedFacebook?: boolean;
  linkedTwitter?: boolean;
  linkedLinkedin?: boolean;
  linkedInstagram?: boolean;
  linkedTiktok?: boolean;
  groups?: Array<Group>;
  registered?: boolean;
  allowBonus?: boolean;
  unsubscribe?: boolean;
  code?: string;
}
export interface EmployeeUpdate extends Omit<Employee, 'groups'> {
  groupsIds: number[];
  groups: {
    [key: string]: {
      name: string;
    };
  };
}
export interface EmployeeCreate extends Omit<Employee, 'id' | 'groups'> {
  id?: number;
  companyId: number;
  password: string;
  groupsIds: number[];
  groups: {
    [key: string]: {
      name: string;
    };
  };
}

export type GroupEmployees = Array<Employee>;

export interface EmployeesTableProps {
  employees: GroupEmployees;
  isAll: boolean;
}

const EmployeesTable: React.FC<EmployeesTableProps> = props => {
  const { employees, isAll } = props;
  const { groupId } = useParams<GroupParams>();
  const { t } = useTranslation(['group_page', 'bonus']);
  const history = useHistory();
  const { currentUser } = useSelector(selectUser);
  const [group, setGroup] = useState<GroupCard>();
  const [visibleVerifiedTooltips, setVisibleVerifiedTooltips] = useState<
    Record<number, boolean>
  >({});
  const [visibleContentTooltips, setVisibleContentTooltips] = useState<
    Record<number, boolean>
  >({});
  const [visibleTooltips, setVisibleTooltips] = useState<{
    [key: string]: boolean;
  }>({});

  const handleVerifiedMouseEnter = (index: number) => {
    setVisibleVerifiedTooltips(prev => ({ ...prev, [index]: true }));
  };

  const handleVerifiedMouseLeave = (index: number) => {
    setVisibleVerifiedTooltips(prev => ({ ...prev, [index]: false }));
  };

  const handleContentMouseEnter = (index: number) => {
    setVisibleContentTooltips(prev => ({ ...prev, [index]: true }));
  };

  const handleContentMouseLeave = (index: number) => {
    setVisibleContentTooltips(prev => ({ ...prev, [index]: false }));
  };
  const getData = async () => {
    const { data } = await getGroupById(currentUser?.token, groupId, 'false');
    setGroup(data.data);
  };

  useEffect(() => {
    getData();
  }, []);

  const go = (id: number) => {
    history.replace(`/admin/group/editEmployee/${groupId}/${id}`);
  };

  const addDefaultPicture = (evt: SyntheticEvent<HTMLImageElement, Event>) => {
    evt.currentTarget.src = EmployeeAvatar;
  };

  const onSubmit = async (
    token: any,
    ambassadorId: any,
    removePermanent: any
  ) => {
    const { data } = await deleteAmbassador(
      token,
      ambassadorId,
      groupId,
      removePermanent
    );
    if (data.data) {
      window.location.reload();
    }
  };

  const confirmAlert = (ambassadorId: any) => {
    Swal.fire({
      text: t('group_page:confirmation_message_ambassador'),
      icon: 'warning',
      input: 'checkbox',
      inputValue: isAll,
      inputPlaceholder: t('group_page:checkbox_alert_ambassador'),
      customClass: {
        input: isAll ? 'hide' : 'show'
      },
      showCancelButton: true,
      confirmButtonText: t('group_page:yes'),
      cancelButtonText: t('group_page:not'),
      reverseButtons: true
    }).then(result => {
      if (result.isConfirmed) {
        onSubmit(currentUser?.token, ambassadorId, Boolean(result.value));
      }
    });
  };

  const renderSocialIcon = (
    index?: number,
    linked?: boolean,
    invalidToken?: string,
    profileLink?: string,
    socialNetwork?: string
  ) => {
    const network = socialNetwork || 'default';
    const tooltipId = `${index}-${network}`;

    let tooltipText = linked
      ? invalidToken === 'true'
        ? t('group_page:Invalid_token')
        : t('group_page:Linked_account')
      : t('group_page:unlinked_account');

    if (network && linked && invalidToken !== 'true') {
      switch (network.toLowerCase()) {
        case 'facebook':
          tooltipText = `${tooltipText}`;
          break;
        case 'twitter':
          tooltipText = `${tooltipText} `;
          break;
        case 'linkedin':
          tooltipText = `${tooltipText} `;
          break;
        case 'instagram':
          tooltipText = `${tooltipText}`;
          break;
        case 'tiktok':
          tooltipText = `${tooltipText}`;
          break;
        default:
      }
    }

    const handleMouseEnter = () => {
      setVisibleTooltips(prev => ({
        ...prev,
        [tooltipId]: true
      }));
    };

    const handleMouseLeave = () => {
      setVisibleTooltips(prev => ({
        ...prev,
        [tooltipId]: false
      }));
    };

    return (
      <StyledTooltipWrapper
        visible={visibleTooltips[tooltipId] || false}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {linked ? (
          invalidToken === 'true' ? (
            <Icon name="alert-rounded" color="orange" size="small" />
          ) : (
            <a
              href={profileLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ cursor: 'pointer' }}
            >
              <Icon name="check-rounded" color="#7ed321" size="small" />
            </a>
          )
        ) : (
          <Icon name="close-rounded" color="red" size="small" />
        )}
        <TooltipMessage>{tooltipText}</TooltipMessage>
      </StyledTooltipWrapper>
    );
  };

  const generateProfileLink = (network: string, userName?: any) => {
    switch (network) {
      case 'facebook':
        return `https://www.facebook.com/${userName}`;
      case 'instagram':
        return `https://www.instagram.com/${userName}`;
      case 'linkedin':
        return `https://www.linkedin.com/in/${userName}`;
      case 'tiktok':
        return `https://www.tiktok.com/@${userName}`;
      case 'twitter':
        return `https://www.x.com/${userName}`;
      default:
        return '';
    }
  };

  return (
    <Table className="notranslate">
      <thead>
        <Row>
          <Th />
          <UserNameHeader>{t('group_page:name')}</UserNameHeader>
          <UserNameHeader>{t('group_page:last_name')}</UserNameHeader>
          <UserNameHeader className="textColumn">
            {t('group_page:email')}
          </UserNameHeader>
          <UserNameHeader className="text-center">
            {t('bonus:bonus_title')}
          </UserNameHeader>
          <UserNameHeader className="text-center">
            <SocialMediaIcon name={'facebook'} size="medium" />
          </UserNameHeader>
          <UserNameHeader className="text-center">
            <SocialMediaIcon name={'twitter'} size="medium" />
          </UserNameHeader>
          <UserNameHeader className="text-center">
            <SocialMediaIcon name={'linkedin'} size="medium" />
          </UserNameHeader>
          <UserNameHeader className="text-center">
            <SocialMediaIcon name={'instagram'} size="medium" />
          </UserNameHeader>
          <UserNameHeader className="text-center">
            <SocialMediaIcon name={'tiktok'} size="medium" />
          </UserNameHeader>
          <UserNameHeader />
          <UserNameHeader />
          <Th />
        </Row>
      </thead>
      <tbody>
        {employees.map((ambassador, index) => {
          return (
            <Row key={index}>
              <PositionColumn collapsed>
                <div className="id">
                  <img
                    src={ambassador.photo || EmployeeAvatar}
                    className="user-photo"
                    alt="User"
                    onError={evt => addDefaultPicture(evt)}
                  />
                </div>
                {ambassador.registered === true && (
                  <StyledTooltipWrapper
                    onMouseEnter={() => handleVerifiedMouseEnter(index)}
                    onMouseLeave={() => handleVerifiedMouseLeave(index)}
                    visible={!!visibleVerifiedTooltips[index]}
                  >
                    <Status>
                      <StatusIcon variant={'verified'} />
                      <TooltipMessage>
                        {t('group_page:Verified_user')}
                      </TooltipMessage>
                    </Status>
                  </StyledTooltipWrapper>
                )}
              </PositionColumn>

              <Column>{ambassador.name}</Column>
              <Column>{ambassador.lastName}</Column>
              <Column>
                <p>{ambassador.email}</p>
              </Column>
              <Column className="text-center">
                {ambassador.allowBonus ? (
                  <StyledTooltipWrapper
                    onMouseEnter={() => handleContentMouseEnter(index)}
                    onMouseLeave={() => handleContentMouseLeave(index)}
                    visible={!!visibleContentTooltips[index]}
                  >
                    <Icon name="check-rounded" color="#d113fe" size="small" />
                    <TooltipMessage>
                      {t('group_page:content_enabled')}
                    </TooltipMessage>
                  </StyledTooltipWrapper>
                ) : (
                  <StyledTooltipWrapper
                    onMouseEnter={() => handleContentMouseEnter(index)}
                    onMouseLeave={() => handleContentMouseLeave(index)}
                    visible={!!visibleContentTooltips[index]}
                  >
                    <Icon name="close-rounded" color="red" size="small" />
                    <TooltipMessage>
                      {t('group_page:content_disabled')}
                    </TooltipMessage>
                  </StyledTooltipWrapper>
                )}
              </Column>
              <Column className="text-center">
                {renderSocialIcon(
                  index,
                  ambassador.linkedFacebook,
                  ambassador.social_network?.fbInvalidToken,
                  generateProfileLink(
                    'facebook',
                    ambassador.social_network?.fbUserName
                  ),
                  'facebook'
                )}
              </Column>
              <Column className="text-center">
                {renderSocialIcon(
                  index,
                  ambassador.linkedTwitter,
                  ambassador.social_network?.xInvalidToken,
                  generateProfileLink(
                    'twitter',
                    ambassador.social_network?.xUserName
                  ),
                  'twitter'
                )}
              </Column>
              <Column className="text-center">
                {renderSocialIcon(
                  index,
                  ambassador.linkedLinkedin,
                  ambassador.social_network?.lnInvalidToken,
                  generateProfileLink(
                    'linkedin',
                    ambassador.social_network?.lnUserName
                  ),
                  'linkedin'
                )}
              </Column>
              <Column className="text-center">
                {renderSocialIcon(
                  index,
                  ambassador.linkedInstagram,
                  ambassador.social_network?.inInvalidToken,
                  generateProfileLink(
                    'instagram',
                    ambassador.social_network?.inUserName
                  ),
                  'instagram'
                )}
              </Column>
              <Column className="text-center">
                {renderSocialIcon(
                  index,
                  ambassador?.linkedTiktok,
                  ambassador.social_network?.tkInvalidToken,
                  generateProfileLink(
                    'tiktok',
                    ambassador.social_network?.tkUserName
                  ),
                  'tiktok'
                )}
              </Column>
              <Column className="text-center">
                <Icon
                  name="edit"
                  color={Colors.primaryDarkBlue}
                  size="medium"
                  onClick={() => go(ambassador.id)}
                />
              </Column>
              <Column className="text-center">
                <img
                  alt="iconTrash"
                  src={iconTrash}
                  onClick={() => confirmAlert(ambassador.id)}
                />
              </Column>
            </Row>
          );
        })}
      </tbody>
    </Table>
  );
};

export default EmployeesTable;
